/**
 * Financial Year Settings (temporary master UI).
 *
 * Persistence today: organisation `hr_settings` via existing
 * GET/PATCH `/organisation/me/hr-settings` (nested under `payroll.financial_years`).
 * No dedicated DB table and no new backend APIs.
 *
 * TODO(backend): Promote Financial Year to its own master entity/table
 * (e.g. `financial_years`) with CRUD APIs when schema changes are allowed.
 * Migrate data out of `hr_settings.payroll.financial_years` and stop using
 * this nested JSON storage.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarRange,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Search,
  Sparkles,
  X,
} from "lucide-react";

import API from "../../services/api";
import BrixigoPageLoader from "../common/BrixigoPageLoader";
import DatePickerField from "../common/DatePickerField";
import ConfirmDialog from "../ui/ConfirmDialog";
import { ToastStack, useToastStack } from "../ui/ToastStack";
import {
  HrAlert,
  HrBtn,
  HrEmptyState,
  HrInput,
  HrPageShell,
  HrPanel,
} from "../hr/HrDesignSystem";
import { navigateToSettingsHome } from "./settingsExit";
import "../../styles/settingsHub.css";

/** TODO(backend): Replace with financial_years master table once APIs exist. */
const FY_STORAGE_KEY = "financial_years";

const CYCLE_META = {
  april: { label: "April – March (India)", startMonth: 4 },
  january: { label: "January – December", startMonth: 1 },
  july: { label: "July – June", startMonth: 7 },
};

function newId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `fy_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function toIsoDate(y, m, d) {
  return `${y}-${pad(m)}-${pad(d)}`;
}

function daysInMonth(y, m) {
  return new Date(y, m, 0).getDate();
}

function formatDisplay(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function formatApiError(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => (typeof item === "string" ? item : item?.msg || JSON.stringify(item))).join("; ");
  }
  if (typeof detail === "object") return detail.message || detail.msg || JSON.stringify(detail);
  return fallback;
}

/** Build default FY window for a given cycle and calendar date. */
function deriveFyForDate(cycleKey, refDate = new Date()) {
  const cycle = CYCLE_META[cycleKey] || CYCLE_META.april;
  const startMonth = cycle.startMonth;
  const y = refDate.getFullYear();
  const m = refDate.getMonth() + 1;
  const startYear = m >= startMonth ? y : y - 1;
  const endYear = startYear + 1;
  const endMonth = startMonth === 1 ? 12 : startMonth - 1;
  const endDay = daysInMonth(endYear, endMonth);
  const shortEnd = String(endYear).slice(-2);
  const name =
    startMonth === 1
      ? `FY ${startYear}`
      : `FY ${startYear}-${shortEnd}`;
  return {
    id: newId(),
    name,
    start_date: toIsoDate(startYear, startMonth, 1),
    end_date: toIsoDate(endYear, endMonth, endDay),
    is_active: true,
  };
}

function validateForm(form, existing, editingId) {
  const errors = {};
  const name = (form.name || "").trim();
  if (!name) errors.name = "Financial year name is required";
  else if (name.length > 64) errors.name = "Name must be 64 characters or less";

  if (!form.start_date) errors.start_date = "Start date is required";
  if (!form.end_date) errors.end_date = "End date is required";
  if (form.start_date && form.end_date && form.start_date >= form.end_date) {
    errors.end_date = "End date must be after start date";
  }

  const overlap = (existing || []).some((row) => {
    if (editingId && row.id === editingId) return false;
    if (!form.start_date || !form.end_date || !row.start_date || !row.end_date) return false;
    return form.start_date <= row.end_date && form.end_date >= row.start_date;
  });
  if (overlap) errors.start_date = errors.start_date || "Dates overlap an existing financial year";

  return errors;
}

function emptyForm() {
  return { name: "", start_date: "", end_date: "" };
}

export default function FinancialYearSettings() {
  const navigate = useNavigate();
  const { toasts, addToast, removeToast } = useToastStack();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [years, setYears] = useState([]);
  const [cycleKey, setCycleKey] = useState("april");
  const [search, setSearch] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});

  const [activateTarget, setActivateTarget] = useState(null);

  const persistYears = useCallback(async (nextYears) => {
    // Temporary: nested JSON in hr_settings — see file-level TODO(backend).
    const payload = {
      payroll: {
        [FY_STORAGE_KEY]: nextYears,
      },
    };
    const res = await API.patch("/organisation/me/hr-settings", payload);
    const payroll = res.data?.payroll_settings || res.data?.hr_settings?.payroll || {};
    const saved = Array.isArray(payroll[FY_STORAGE_KEY]) ? payroll[FY_STORAGE_KEY] : nextYears;
    setYears(saved);
    return saved;
  }, []);

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const [hrRes, setupRes] = await Promise.all([
        API.get("/organisation/me/hr-settings"),
        API.get("/setup/status").catch(() => null),
      ]);
      const hr = hrRes.data?.hr_settings || {};
      const payroll = hrRes.data?.payroll_settings || hr.payroll || {};
      const org = setupRes?.data?.org || {};
      const cycle = String(org.financial_year_start || hr.financial_year_start || "april").toLowerCase();
      setCycleKey(CYCLE_META[cycle] ? cycle : "april");

      let list = Array.isArray(payroll[FY_STORAGE_KEY]) ? payroll[FY_STORAGE_KEY] : [];
      if (!list.length) {
        const seeded = [deriveFyForDate(CYCLE_META[cycle] ? cycle : "april")];
        list = await persistYears(seeded);
        addToast("Default financial year created from organisation cycle", "success");
      }
      setYears(list);
    } catch (err) {
      setError(formatApiError(err, "Failed to load financial years"));
    } finally {
      setLoading(false);
    }
  }, [addToast, persistYears]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...years].sort((a, b) => String(b.start_date || "").localeCompare(String(a.start_date || "")));
    if (!q) return sorted;
    return sorted.filter(
      (row) =>
        String(row.name || "").toLowerCase().includes(q) ||
        String(row.start_date || "").includes(q) ||
        String(row.end_date || "").includes(q)
    );
  }, [years, search]);

  const activeYear = useMemo(() => years.find((y) => y.is_active) || null, [years]);

  const openCreate = () => {
    const draft = deriveFyForDate(cycleKey);
    setEditing(null);
    setForm({ name: draft.name, start_date: draft.start_date, end_date: draft.end_date });
    setFormErrors({});
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      start_date: row.start_date || "",
      end_date: row.end_date || "",
    });
    setFormErrors({});
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setEditing(null);
    setForm(emptyForm());
    setFormErrors({});
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    const errors = validateForm(form, years, editing?.id);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    setSaving(true);
    try {
      let next;
      if (editing?.id) {
        next = years.map((row) =>
          row.id === editing.id
            ? {
                ...row,
                name: form.name.trim(),
                start_date: form.start_date,
                end_date: form.end_date,
              }
            : row
        );
      } else {
        const makeActive = years.length === 0;
        next = [
          ...years.map((row) => (makeActive ? { ...row, is_active: false } : row)),
          {
            id: newId(),
            name: form.name.trim(),
            start_date: form.start_date,
            end_date: form.end_date,
            is_active: makeActive,
          },
        ];
      }
      await persistYears(next);
      addToast(editing ? "Financial year updated" : "Financial year created", "success");
      closeDrawer();
      navigateToSettingsHome(navigate, { replace: true });
    } catch (err) {
      addToast(formatApiError(err, "Failed to save financial year"), "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmActivate = async () => {
    if (!activateTarget) return;
    setSaving(true);
    try {
      const next = years.map((row) => ({
        ...row,
        is_active: row.id === activateTarget.id,
      }));
      await persistYears(next);
      addToast(`“${activateTarget.name}” is now the active financial year`, "success");
      setActivateTarget(null);
      navigateToSettingsHome(navigate, { replace: true });
    } catch (err) {
      addToast(formatApiError(err, "Failed to activate financial year"), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <HrPageShell>
        <BrixigoPageLoader label="Loading financial years…" />
      </HrPageShell>
    );
  }

  return (
    <HrPageShell>
      <ToastStack toasts={toasts} onDismiss={removeToast} />

      <div className="settings-platform max-w-6xl mx-auto px-4 py-6 md:px-6">
        <nav className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500 mb-4" aria-label="Breadcrumb">
          <Link to="/settings" className="hover:text-teal-700 transition">
            Settings
          </Link>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          <span className="text-slate-500">Organization</span>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          <span className="font-semibold text-slate-800">Financial Year</span>
        </nav>

        <header className="settings-hero mb-6">
          <div className="settings-hero-inner">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="settings-hero-eyebrow">
                  <CalendarRange className="h-3.5 w-3.5" />
                  Organization
                </div>
                <h1 className="settings-hero-title">Financial Year</h1>
                <p className="settings-hero-sub">
                  Define accounting and payroll years. Activate one year for payroll processing, TDS, and reports.
                </p>
              </div>
              <HrBtn type="button" onClick={openCreate}>
                <Plus className="h-4 w-4" />
                Add Financial Year
              </HrBtn>
            </div>
            <div className="settings-hero-stats">
              <div className="settings-hero-stat">
                <Sparkles className="h-4 w-4 opacity-80" />
                <div>
                  <div className="text-[10px] uppercase tracking-wide opacity-75 font-semibold">Active year</div>
                  <div className="font-bold">{activeYear?.name || "None"}</div>
                </div>
              </div>
              <div className="settings-hero-stat">
                <CalendarRange className="h-4 w-4 opacity-80" />
                <div>
                  <div className="text-[10px] uppercase tracking-wide opacity-75 font-semibold">Org cycle</div>
                  <div className="font-bold text-sm">{CYCLE_META[cycleKey]?.label || "April – March"}</div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {error ? (
          <HrAlert variant="error" className="mb-4">
            {error}
            <button type="button" className="ml-3 underline font-semibold" onClick={load}>
              Retry
            </button>
          </HrAlert>
        ) : null}

        <HrPanel
          title="Financial years"
          subtitle="Used across payroll, TDS, and statutory reports"
          headerRight={
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" aria-hidden />
              <HrInput
                type="search"
                placeholder="Search by name or date…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search financial years"
              />
            </div>
          }
        >
          {filtered.length === 0 ? (
            <HrEmptyState
              icon={CalendarRange}
              title={search ? "No matching financial years" : "No financial years yet"}
              description={
                search
                  ? "Try a different search term."
                  : "Create your first financial year to start payroll and compliance reporting."
              }
            />
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-3 px-3 font-semibold">Name</th>
                    <th className="py-3 px-3 font-semibold">Start Date</th>
                    <th className="py-3 px-3 font-semibold">End Date</th>
                    <th className="py-3 px-3 font-semibold">Status</th>
                    <th className="py-3 px-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                      <td className="py-3 px-3 font-semibold text-slate-800">{row.name}</td>
                      <td className="py-3 px-3 tabular-nums text-slate-600">{formatDisplay(row.start_date)}</td>
                      <td className="py-3 px-3 tabular-nums text-slate-600">{formatDisplay(row.end_date)}</td>
                      <td className="py-3 px-3">
                        {row.is_active ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 px-2.5 py-0.5 text-xs font-semibold">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-2.5 py-0.5 text-xs font-medium">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            onClick={() => openEdit(row)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </button>
                          {!row.is_active ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-teal-700"
                              onClick={() => setActivateTarget(row)}
                            >
                              Activate
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </HrPanel>
      </div>

      {drawerOpen ? (
        <div
          className="fixed inset-0 z-[9000] flex justify-end bg-slate-900/40 backdrop-blur-[1px]"
          role="presentation"
          onClick={closeDrawer}
        >
          <aside
            className="h-full w-full max-w-md bg-white shadow-2xl border-l border-slate-200 flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fy-drawer-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <h2 id="fy-drawer-title" className="text-lg font-bold text-slate-900">
                  {editing ? "Edit Financial Year" : "Add Financial Year"}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">Name, start date, and end date</p>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                onClick={closeDrawer}
                aria-label="Close"
                disabled={saving}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form className="flex-1 overflow-y-auto px-5 py-5 space-y-4" onSubmit={handleSave}>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor="fy-name">
                  Financial Year Name <span className="text-red-500">*</span>
                </label>
                <HrInput
                  id="fy-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. FY 2026-27"
                  aria-invalid={Boolean(formErrors.name)}
                />
                {formErrors.name ? <p className="mt-1 text-xs text-red-600">{formErrors.name}</p> : null}
              </div>

              <DatePickerField
                label="Start Date"
                required
                value={form.start_date}
                onChange={(v) => setForm((f) => ({ ...f, start_date: v }))}
                error={formErrors.start_date}
              />

              <DatePickerField
                label="End Date"
                required
                value={form.end_date}
                onChange={(v) => setForm((f) => ({ ...f, end_date: v }))}
                error={formErrors.end_date}
              />
            </form>

            <div className="border-t border-slate-200 px-5 py-4 flex flex-wrap justify-end gap-2">
              <HrBtn type="button" variant="secondary" onClick={closeDrawer} disabled={saving}>
                Cancel
              </HrBtn>
              <HrBtn type="button" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {saving ? "Saving…" : "Save"}
              </HrBtn>
            </div>
          </aside>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(activateTarget)}
        title="Activate financial year?"
        message={
          activateTarget
            ? `Make “${activateTarget.name}” the active year for payroll, TDS, and reports? The current active year will be deactivated.`
            : ""
        }
        confirmLabel="Activate"
        onConfirm={confirmActivate}
        onCancel={() => !saving && setActivateTarget(null)}
        loading={saving}
      />
    </HrPageShell>
  );
}
